// クリエイター一覧（design/README.md §3）。行クリックで右パネル（CreatorPanel は GiftingPage が出す）。
import { useState, type KeyboardEvent } from "react";
import type { GfPlatform, GfTabProps } from "../../../types/gifting";
import { fmtFollowers, fmtPct, handleOf, primarySns } from "../../../lib/giftingRules";
import { GfAvatar, GfButton, GfCard, GfEmpty, GfMono, GfTag } from "../ui/GfUi";
import { CreatorFormModal } from "../modals/CreatorFormModal";
import { affMapOf, payeeTag, snsOfPlatform } from "./kit/helpers";

const MEDIA_COLS: Array<{ platform: GfPlatform; label: string }> = [
  { platform: "instagram", label: "Instagram" },
  { platform: "youtube", label: "YouTube" },
  { platform: "tiktok", label: "TikTok" },
  { platform: "ameblo", label: "Ameblo" },
];

export function CreatorsTab(props: GfTabProps) {
  const { data, nav } = props;
  const [formOpen, setFormOpen] = useState(false);
  const affs = affMapOf(data.affiliates);
  const creators = data.board.creators;

  const onRowKey = (e: KeyboardEvent, id: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      nav.openCreator(id);
    }
  };

  const rateCell = (id: string): string => {
    if (data.affiliates.status === "loading") return "…";
    const a = affs.get(id);
    return a ? fmtPct(a.commission_rate) : "—";
  };

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">クリエイター</h1>
        <div className="gf-actions">
          <GfButton variant="primary" onClick={() => setFormOpen(true)}>
            クリエイターを追加
          </GfButton>
        </div>
      </div>

      <GfCard flush>
        <div className="gf-table-wrap">
          <table className="gf-table gf-creators-table">
            <thead>
              <tr>
                <th>名前</th>
                {MEDIA_COLS.map((m) => (
                  <th key={m.platform}>{m.label}</th>
                ))}
                <th>ジャンル</th>
                <th>個別料率</th>
                <th>口座</th>
              </tr>
            </thead>
            <tbody>
              {creators.map((c) => {
                const tag = payeeTag(c.payee_status);
                const main = primarySns(c.sns);
                return (
                  <tr
                    key={c.id}
                    className={`is-click${nav.state.creator === c.id ? " is-selected" : ""}`}
                    tabIndex={0}
                    onClick={() => nav.openCreator(c.id)}
                    onKeyDown={(e) => onRowKey(e, c.id)}
                  >
                    <td>
                      <div className="gf-name-cell">
                        <GfAvatar name={c.name} size={30} />
                        <span className="gf-name-cell-text">
                          <span className="gf-name-strong">{c.name}</span>
                          <span className="gf-handle">{handleOf(main) || "—"}</span>
                        </span>
                      </div>
                    </td>
                    {MEDIA_COLS.map((m) => {
                      const s = snsOfPlatform(c.sns, m.platform);
                      return (
                        <td key={m.platform}>
                          <GfMono>{s ? fmtFollowers(s.follower_count) : "—"}</GfMono>
                        </td>
                      );
                    })}
                    <td>{c.genre || <span className="gf-faint">—</span>}</td>
                    <td>
                      <GfMono>{rateCell(c.id)}</GfMono>
                    </td>
                    <td>
                      <GfTag tone={tag.tone}>{tag.label}</GfTag>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {creators.length === 0 && <GfEmpty>クリエイターがまだ登録されていません。「クリエイターを追加」から登録できます。</GfEmpty>}
      </GfCard>

      <CreatorFormModal
        {...props}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(id) => {
          setFormOpen(false);
          nav.openCreator(id);
        }}
      />
    </>
  );
}
